import React from 'react';
import { X, Clock, AlertTriangle, CheckCircle, FileText, Brain, CheckSquare, Timer } from 'lucide-react';

const SubmissionConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    task,
    activeTime,
    validationSummary,
    isSubmitting
}) => {
    if (!isOpen) return null;

    const formatTime = (ms) => {
        const totalSeconds = Math.max(0, Math.floor(ms / 1000));
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`;
        }
        return `${minutes}m ${seconds}s`;
    };

    const getTaskTypeIcon = () => {
        switch (task?.task_type) {
            case 'quiz': return Brain;
            case 'checklist': return CheckSquare;
            default: return FileText;
        }
    };

    const TaskIcon = getTaskTypeIcon();

    const isQuiz = task?.task_type === 'quiz' && task?.quiz_grading_type !== 'manual';

    return (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary to-primary-hover p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <TaskIcon className="w-6 h-6" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold">Confirm Submission</h2>
                                <p className="text-white/80 text-sm">Review before submitting</p>
                            </div>
                        </div>
                        {!isQuiz && (
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Time Summary */}
                    <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Timer className="w-5 h-5 text-primary" />
                            <h3 className="font-semibold text-gray-800">Time Summary</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Active Work Time</p>
                                <p className="text-xl font-bold text-primary">{formatTime(activeTime)}</p>
                            </div>
                            {task?.estimated_duration && (
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Estimated</p>
                                    <p className="text-xl font-bold text-gray-700">{task.estimated_duration}m</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Submission Checklist */}
                    <div>
                        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            What You're Submitting
                        </h3>
                        <div className="space-y-2">
                            {validationSummary?.items?.map((item, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    <span className="text-gray-700">{item}</span>
                                </div>
                            ))}
                            {!validationSummary?.items?.length && (
                                <div className="flex items-center gap-2 text-sm">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span className="text-gray-700">Task completed</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-amber-800">Important</p>
                                <p className="text-sm text-amber-700">
                                    You cannot edit your answers after submission. Make sure you've reviewed everything carefully.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 pb-6 flex gap-3">
                    {!isQuiz && (
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        onClick={onConfirm}
                        disabled={isSubmitting}
                        className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <CheckCircle className="w-5 h-5" />
                                {isQuiz ? 'Confirm Submission' : 'Submit Task'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubmissionConfirmModal;
